import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Breadcrumb,
  Modal,
  Input,
  message,
  Popconfirm,
  Image,
  Row,
  Col,
  Empty,
  Alert,
  Tooltip,
  Tag,
  Progress
} from 'antd';
import {
  UploadOutlined,
  FolderAddOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FolderOutlined,
  FileOutlined,
  EyeOutlined,
  LinkOutlined,
  LeftOutlined,
  HomeOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { initCOS, clearAuth } from '../utils/auth';
import './FileManager.css';

const FileManager = ({ userInfo, customDomain }) => {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [folderModalVisible, setFolderModalVisible] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState('image'); // image, audio, video
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [cosInstance, setCosInstance] = useState(null);
  const [bucketInfo, setBucketInfo] = useState({ bucket: '', region: '' });

  // 权限检查
  const canRead = userInfo?.permissions?.includes('read');
  const canWrite = userInfo?.permissions?.includes('write');
  const canDelete = userInfo?.permissions?.includes('delete');

  // 初始化 COS
  useEffect(() => {
    const init = async () => {
      try {
        const { cos, bucket, region } = await initCOS();
        setCosInstance(cos);
        setBucketInfo({ bucket, region });
      } catch (error) {
        message.error('初始化失败: ' + error.message);
        if (error.message?.includes('未登录')) {
          clearAuth();
          window.location.reload();
        }
      }
    };
    init();
  }, []);

  // 获取文件列表
  const fetchFiles = useCallback(async () => {
    if (!cosInstance) return;

    setLoading(true);
    try {
      const result = await cosInstance.getBucket({
        Bucket: bucketInfo.bucket,
        Region: bucketInfo.region,
        Prefix: currentPath,
        Delimiter: '/'
      });

      const contents = (result.Contents || []).filter(
        item => item.Key !== currentPath
      );
      setFiles(contents);
      setFolders(result.CommonPrefixes || []);
    } catch (error) {
      message.error('获取文件列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [cosInstance, currentPath, bucketInfo]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // 上传多个文件
  const handleUpload = async (files) => {
    if (!canWrite) {
      message.warning('您没有上传权限');
      return;
    }

    // 转换为数组
    const fileList = Array.from(files);
    if (fileList.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadFileName(`共 ${fileList.length} 个文件`);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const key = currentPath + file.name;
      
      setUploadFileName(`正在上传 (${i + 1}/${fileList.length}): ${file.name}`);

      try {
        await cosInstance.putObject({
          Bucket: bucketInfo.bucket,
          Region: bucketInfo.region,
          Key: key,
          Body: file,
          onProgress: (progressData) => {
            // 总体进度 = 已完成文件 + 当前文件进度
            const fileProgress = progressData.percent;
            const totalProgress = ((i + fileProgress) / fileList.length) * 100;
            setUploadProgress(Math.round(totalProgress));
          }
        });
        successCount++;
      } catch (error) {
        console.error(`上传失败: ${file.name}`, error);
        failCount++;
      }
    }

    // 显示结果
    if (failCount === 0) {
      message.success(`成功上传 ${successCount} 个文件`);
    } else {
      message.warning(`上传完成: ${successCount} 个成功, ${failCount} 个失败`);
    }

    setUploading(false);
    setUploadProgress(0);
    setUploadFileName('');
    fetchFiles();
  };

  // 删除文件
  const handleDelete = async (key) => {
    if (!canDelete) {
      message.warning('您没有删除权限');
      return;
    }

    try {
      await cosInstance.deleteObject({
        Bucket: bucketInfo.bucket,
        Region: bucketInfo.region,
        Key: key
      });
      message.success('删除成功');
      fetchFiles();
    } catch (error) {
      message.error('删除失败: ' + error.message);
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (!canDelete) {
      message.warning('您没有删除权限');
      return;
    }

    if (selectedRows.length === 0) {
      message.warning('请选择要删除的文件');
      return;
    }

    const keys = selectedRows.map(row => ({ Key: row.Key }));

    try {
      await cosInstance.deleteMultipleObject({
        Bucket: bucketInfo.bucket,
        Region: bucketInfo.region,
        Objects: keys,
        Quiet: true
      });
      message.success('批量删除成功');
      setSelectedRows([]);
      fetchFiles();
    } catch (error) {
      message.error('批量删除失败: ' + error.message);
    }
  };

  // 创建文件夹
  const handleCreateFolder = async () => {
    if (!canWrite) {
      message.warning('您没有创建文件夹权限');
      return;
    }

    if (!folderName.trim()) {
      message.warning('请输入文件夹名称');
      return;
    }

    const key = currentPath + folderName.trim() + '/';

    try {
      await cosInstance.putObject({
        Bucket: bucketInfo.bucket,
        Region: bucketInfo.region,
        Key: key,
        Body: ''
      });
      message.success('创建成功');
      setFolderModalVisible(false);
      setFolderName('');
      fetchFiles();
    } catch (error) {
      message.error('创建失败: ' + error.message);
    }
  };

  // 获取文件 URL（支持自定义域名）
  const getFileUrl = (key, signed = true) => {
    if (customDomain) {
      // 使用自定义域名，拼接 URL
      return `${customDomain.replace(/\/$/, '')}/${key}`;
    }
    // 使用 COS 默认域名
    return cosInstance.getObjectUrl({
      Bucket: bucketInfo.bucket,
      Region: bucketInfo.region,
      Key: key,
      Expires: 3600,
      Sign: signed
    });
  };

  // 预览文件（图片、音频、视频）
  const handlePreview = async (key) => {
    const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(key);
    const isAudio = /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(key);
    const isVideo = /\.(mp4|webm|ogv|mov|mkv|avi|flv|m3u8)$/i.test(key);
    
    if (!isImage && !isAudio && !isVideo) {
      message.info('该文件类型不支持预览');
      return;
    }

    try {
      const url = getFileUrl(key, true);
      setPreviewUrl(url);
      setPreviewType(isVideo ? 'video' : isAudio ? 'audio' : 'image');
      setPreviewVisible(true);
    } catch (error) {
      message.error('获取预览链接失败');
    }
  };

  // 复制链接
  const handleCopyLink = async (key) => {
    try {
      const url = getFileUrl(key, true);
      navigator.clipboard.writeText(url);
      message.success('链接已复制到剪贴板');
    } catch (error) {
      message.error('获取链接失败');
    }
  };

  // 进入文件夹
  const enterFolder = (prefix) => {
    setCurrentPath(prefix);
    setSelectedRows([]);
  };

  // 返回上级
  const goBack = () => {
    if (!currentPath) return;
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    setCurrentPath(parts.length > 0 ? parts.join('/') + '/' : '');
    setSelectedRows([]);
  };

  // 表格列定义
  const columns = [
    {
      title: '名称',
      dataIndex: 'Key',
      key: 'name',
      render: (key, record) => {
        const isFolder = key.endsWith('/');
        const name = isFolder ? key.slice(currentPath.length, -1) : key.slice(currentPath.length);
        return (
          <Space>
            {isFolder ? <FolderOutlined style={{ color: '#faad14' }} /> : <FileOutlined />}
            <span>{name}</span>
          </Space>
        );
      }
    },
    {
      title: '大小',
      dataIndex: 'Size',
      key: 'size',
      width: 120,
      render: (size, record) => {
        if (record.Key.endsWith('/')) return '-';
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
        if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(2)} MB`;
        return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
      }
    },
    {
      title: '修改时间',
      dataIndex: 'LastModified',
      key: 'modified',
      width: 180,
      render: (time) => time ? new Date(time).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          {!record.Key.endsWith('/') && (
            <>
              {(/\.(jpg|jpeg|png|gif|webp|bmp|mp3|wav|ogg|m4a|aac|flac|mp4|webm|ogv|mov|mkv|avi|flv|m3u8)$/i.test(record.Key)) && (
                <Tooltip title="预览">
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handlePreview(record.Key)}
                  />
                </Tooltip>
              )}
              <Tooltip title="复制链接">
                <Button
                  type="text"
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={() => handleCopyLink(record.Key)}
                />
              </Tooltip>
            </>
          )}
          {canDelete && (
            <Popconfirm
              title="确定删除吗？"
              onConfirm={() => handleDelete(record.Key)}
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  // 合并文件夹和文件数据
  const tableData = [
    ...folders.map(prefix => ({ Key: prefix.Prefix, isFolder: true })),
    ...files
  ];

  return (
    <div className="file-manager">
      {/* 权限提示 */}
      <Alert
        message="权限说明"
        description={
          <span>
            当前权限: {' '}
            {canRead && <Tag color="blue">读</Tag>}
            {canWrite && <Tag color="green">写</Tag>}
            {canDelete && <Tag color="red">删</Tag>}
            {' '} | 存储桶: {bucketInfo.bucket}
            {customDomain && <Tag color="purple" style={{ marginLeft: 8 }}>CDN: {customDomain}</Tag>}
          </span>
        }
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
      />

      <Row gutter={[16, 16]} className="toolbar">
        <Col flex="auto">
          <Space>
            <Button icon={<LeftOutlined />} onClick={goBack} disabled={!currentPath}>
              返回上级
            </Button>
          </Space>
        </Col>
        <Col>
          <Space>
            {canWrite && (
              <>
                <input
                  type="file"
                  id="file-input"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      handleUpload(e.target.files);
                      e.target.value = '';
                    }
                  }}
                />
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  loading={uploading}
                  onClick={() => document.getElementById('file-input').click()}
                >
                  上传文件
                </Button>

                <Button
                  icon={<FolderAddOutlined />}
                  onClick={() => setFolderModalVisible(true)}
                >
                  新建文件夹
                </Button>
              </>
            )}

            {canDelete && (
              <Popconfirm
                title={`确定删除选中的 ${selectedRows.length} 个文件吗？`}
                onConfirm={handleBatchDelete}
                disabled={selectedRows.length === 0}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={selectedRows.length === 0}
                >
                  批量删除
                </Button>
              </Popconfirm>
            )}

            <Button icon={<ReloadOutlined />} onClick={fetchFiles}>
              刷新
            </Button>
          </Space>
        </Col>
      </Row>

      <Breadcrumb className="breadcrumb">
        <Breadcrumb.Item>
          <HomeOutlined onClick={() => setCurrentPath('')} style={{ cursor: 'pointer' }} />
        </Breadcrumb.Item>
        {currentPath.split('/').filter(Boolean).map((part, index) => (
          <Breadcrumb.Item key={index}>{part}</Breadcrumb.Item>
        ))}
      </Breadcrumb>

      <Table
        rowKey="Key"
        columns={columns}
        dataSource={tableData}
        loading={loading}
        rowSelection={canDelete ? {
          selectedRowKeys: selectedRows.map(row => row.Key),
          onChange: (_, rows) => setSelectedRows(rows),
          getCheckboxProps: (record) => ({
            disabled: record.Key.endsWith('/')
          })
        } : undefined}
        onRow={(record) => ({
          onClick: () => {
            if (record.Key.endsWith('/')) {
              enterFolder(record.Key);
            }
          }
        })}
        locale={{
          emptyText: <Empty description="暂无文件" />
        }}
      />

      {/* 创建文件夹弹窗 */}
      <Modal
        title="新建文件夹"
        open={folderModalVisible}
        onOk={handleCreateFolder}
        onCancel={() => {
          setFolderModalVisible(false);
          setFolderName('');
        }}
      >
        <Input
          placeholder="请输入文件夹名称"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          onPressEnter={handleCreateFolder}
        />
      </Modal>

      {/* 图片预览 */}
      {previewType === 'image' && (
        <Image
          style={{ display: 'none' }}
          src={previewUrl}
          preview={{
            visible: previewVisible,
            onVisibleChange: setPreviewVisible,
          }}
        />
      )}

      {/* 音频预览弹窗 */}
      <Modal
        title="音频预览"
        open={previewVisible && previewType === 'audio'}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={500}
      >
        <audio
          controls
          src={previewUrl}
          style={{ width: '100%', marginTop: 16 }}
          autoPlay
        >
          您的浏览器不支持音频播放
        </audio>
      </Modal>

      {/* 视频预览弹窗 */}
      <Modal
        title="视频预览"
        open={previewVisible && previewType === 'video'}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
        centered
      >
        <video
          controls
          src={previewUrl}
          style={{ width: '100%', marginTop: 16, maxHeight: '60vh' }}
          autoPlay
          playsInline
        >
          您的浏览器不支持视频播放
        </video>
      </Modal>

      {/* 上传进度弹窗 */}
      <Modal
        title="文件上传中"
        open={uploading}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={400}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ marginBottom: 16 }}>
            <FileOutlined style={{ marginRight: 8 }} />
            {uploadFileName}
          </p>
          <Progress
            percent={uploadProgress}
            status={uploadProgress === 100 ? 'success' : 'active'}
            strokeColor={{ from: '#87CEEB', to: '#1E90FF' }}
          />
          <p style={{ textAlign: 'center', marginTop: 8, color: '#888' }}>
            {uploadProgress === 100 ? '上传完成，正在处理...' : `已上传 ${uploadProgress}%`}
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default FileManager;
